/**
 * Choosing a language (SPEC_CAPTURE_UX_R2 §2.2): the ONE path both the chooser and the in-page switcher use — set it
 * in the runtime (which re-renders every visible string and stores it on this device), then persist it on the CASE
 * (`PATCH /locale`) when a capture bearer exists, so a link re-opened on another phone keeps it. The PATCH is best-effort
 * with one retry and never blocks the journey; only an explicit tap reaches here — never the tenant's preference.
 */

import { patchLocale } from '@/api/capture';
import { getBearer } from '@/api/client';

const RETRY_MS = 2_000;
let last: Promise<boolean> = Promise.resolve(false);

export function chooseLocale(code: string, setLocale: (next: string) => void): Promise<boolean> {
  setLocale(code);
  if (!getBearer()) return Promise.resolve(false);
  last = (async () => {
    try {
      await patchLocale(code);
      return true;
    } catch {
      await new Promise((r) => setTimeout(r, RETRY_MS));
      try {
        await patchLocale(code);
        return true;
      } catch {
        return false; // the device keeps the choice; the case will learn it on the next explicit tap
      }
    }
  })();
  return last;
}

/**
 * US-03482 #3 — a choice this device remembers (an explicit tap on an earlier link) skips the chooser, so without this
 * the case never learned it and the reviewer read "language not chosen". When the case carries no choice but the device
 * does, persist the device's choice onto the case — the same best-effort PATCH a tap makes. It is still the customer's
 * own explicit choice, never the tenant's preference; a case that already carries a choice is left alone.
 */
export function carryDeviceChoiceToCase(caseLocale: string | null | undefined, deviceLocale: string | null, setLocale: (next: string) => void): Promise<boolean> {
  if (caseLocale || !deviceLocale) return Promise.resolve(false);
  return chooseLocale(deviceLocale, setLocale);
}

/** Tests await the in-flight persist. */
export function lastLocalePersist(): Promise<boolean> {
  return last;
}
